"use client";

import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { BlockProps } from "../BlocksManager/registry";

export default function FormuleSection({ data }: BlockProps) {
  if (!data) return null;

  const formules = data?.formules || [];
  const desiredOrder = ["Entrée", "Plat", "Laitage", "Dessert"];

  const tabs = useMemo(() => {
    return [...new Set(formules.map((item: any) => item.type))] as string[];
  }, [formules]);

  const orderedTabs = useMemo(() => {
    return [...tabs].sort((a, b) => {
      const indexA = desiredOrder.indexOf(a);
      const indexB = desiredOrder.indexOf(b);

      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  }, [tabs]);

  const [activeTab, setActiveTab] = useState(orderedTabs[0] || "");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!orderedTabs.length) return;

    if (!activeTab || !orderedTabs.includes(activeTab)) {
      setActiveTab(orderedTabs[0]);
      setCurrentIndex(0);
    }
  }, [orderedTabs, activeTab]);

  const filteredFormules = formules.filter(
    (item: any) => item.type === activeTab
  );

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentIndex(0);
  };

  const goLeft = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const goRight = () => {
    setCurrentIndex((prev) =>
      Math.min(filteredFormules.length - 1, prev + 1)
    );
  };

  const iconUrl = data?.icone?.url;
  function renderMarkedTitle(title: string, highlightClass: string) {
    const parts = title.split(/(\[.*?\])/g);

    return parts.map((part, index) => {
      const isHighlighted = part.startsWith("[") && part.endsWith("]");

      if (isHighlighted) {
        const text = part.slice(1, -1);

        return (
          <span
            key={index}
            className={`inline-block font-heading rounded-xl -rotate-1 my-2 uppercase tracking-widest px-2  py-1 text-white ${highlightClass}`}
          >
            {text}
          </span>
        );
      }

      return <span key={index}>{part}</span>;
    });
  }
  return (
    <section className="bg-blue-light py-16">
      <div className="container">
        <div className="text-center">
          <h2 className="font-literata text-[38px] font-bold leading-tight text-text-body lg:text-[54px]">
            {data.titre}
          </h2>

          {iconUrl && (
            <div className="mx-auto mt-6 flex justify-center">
              <div className="relative h-16 w-16 lg:h-20 lg:w-20">
                <Image
                  src={iconUrl}
                  alt={data.icone?.alternativeText || "Icône"}
                  fill
                  className="object-contain"
                />
              </div>
            </div>
          )}

          {data.sous_titre && (
            <div className="mx-auto mt-4 inline-flex items-center gap-2">
              <span className="font-body text-[24px] font-bold text-text-body lg:text-[28px]">
                Offre
              </span>
              <span className="rounded-md font-body text-[24px] font-bold uppercase text-white lg:text-[28px]">
                {renderMarkedTitle(data.sous_titre, "bg-blue")}
              </span>
            </div>
          )}

          <p className="mx-auto mt-6 max-w-[860px] text-[18px] font-semibold leading-[1.35] text-text-body md:text-[24px]">
            {data.description}
          </p>

          <div className="mt-8 inline-flex flex-wrap items-center justify-center rounded-full bg-white px-3 py-2 shadow-sm">
            {orderedTabs.map((tab, index) => (
              <div key={tab} className="flex items-center">
                <button
                  type="button"
                  onClick={() => handleTabChange(tab)}
                  className={`rounded-full px-5 py-2.5 text-[16px] font-semibold transition lg:min-w-[140px] lg:px-8 lg:py-3 lg:text-[20px] ${activeTab === tab
                      ? "bg-blue text-white"
                      : "text-text-body hover:text-blue"
                    }`}
                >
                  {tab}
                </button>

                {index < orderedTabs.length - 1 && (
                  <span className="mx-2 text-blue lg:mx-4">|</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile — carousel par index */}
        <div className="relative mt-14 md:hidden">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {filteredFormules.map((item: any) => (
                <div key={item.id} className="w-full shrink-0 px-4">
                  <article className="mx-auto flex w-full max-w-[340px] flex-col items-center rounded-[200px] bg-white px-6 pb-8 pt-6 text-center">
                    <div className="relative h-[260px] w-[260px]">
                      {item?.image?.url && (
                        <Image
                          src={item.image.url}
                          alt={item.image.alternativeText || item.nom}
                          fill
                          className="object-contain"
                        />
                      )}
                    </div>
                    <h3 className="mt-4 w-full hyphens-auto font-heading text-[24px] font-medium leading-[1.2] text-text-body">
                      {item.nom}
                    </h3>
                  </article>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-6">
            <button
              type="button"
              onClick={goLeft}
              disabled={currentIndex === 0}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-md disabled:opacity-30"
            >
              <ChevronLeft className="h-5 w-5 text-black" />
            </button>

            <div className="flex gap-2">
              {filteredFormules.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setCurrentIndex(i)}
                  className={`h-2 w-2 rounded-full transition-all ${i === currentIndex ? "w-5 bg-blue" : "bg-blue/30"
                    }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={goRight}
              disabled={currentIndex === filteredFormules.length - 1}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-md disabled:opacity-30"
            >
              <ChevronRight className="h-5 w-5 text-black" />
            </button>
          </div>
        </div>

        {/* Desktop — grille */}
        <div className="mt-14 hidden md:grid md:grid-cols-2 md:gap-8 xl:grid-cols-3">
          {filteredFormules.map((item: any) => (
            <article
              key={item.id}
              className="mx-auto flex w-full max-w-[360px] flex-col items-center rounded-[200px] bg-white px-6 pb-8 pt-6 text-center"
            >
              <div className="relative h-[260px] w-[260px] md:h-[300px] md:w-[300px]">
                {item?.image?.url && (
                  <Image
                    src={item.image.url}
                    alt={item.image.alternativeText || item.nom}
                    fill
                    className="object-contain"
                  />
                )}
              </div>
              <h3 className="mt-4 w-full hyphens-auto font-heading text-[24px] font-medium leading-[1.2] text-text-body lg:text-[32px]">
                {item.nom}
              </h3>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}